function safeNumber(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
}

function sum(values = []) {
  return values.reduce((total, value) => total + safeNumber(value), 0);
}

function average(values = []) {
  if (!values.length) {
    return 0;
  }

  return sum(values) / values.length;
}

function normalizeDirection(value) {
  const text = String(value || '').toLowerCase();
  if (text.includes('worsen')) return 'worsening';
  if (text.includes('improv')) return 'improving';
  if (text.includes('stable')) return 'stable';
  return 'unknown';
}

function daysUntil(dateString) {
  if (!dateString) {
    return null;
  }

  const target = new Date(dateString);
  if (Number.isNaN(target.getTime())) {
    return null;
  }

  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfTarget = new Date(target.getFullYear(), target.getMonth(), target.getDate());
  return Math.round((startOfTarget - startOfToday) / 86400000);
}

function formatMoney(amount) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Math.round(safeNumber(amount)));
}

function computeCollectionPotential(tenant) {
  const balance = safeNumber(tenant.currentBalance);
  const ervDollars = safeNumber(tenant.ervDollars);
  if (balance <= 0 || ervDollars <= 0) {
    return { next7Days: 0, next30Days: 0 };
  }

  const expectedDays = daysUntil(tenant.expectedPayDate);
  const consistency = tenant.consistency || 'unknown';
  const driftDirection = tenant.driftDirection || 'unknown';
  const riskTier = tenant.riskTier || 'medium';
  const daysLate = safeNumber(tenant.daysLate);

  let next7Factor = expectedDays !== null && expectedDays <= 7 ? 0.72 : 0.32;
  let next30Factor = expectedDays !== null && expectedDays <= 30 ? 0.88 : 0.58;

  if (consistency === 'very_consistent') {
    next7Factor += 0.08;
    next30Factor += 0.05;
  } else if (consistency === 'unpredictable') {
    next7Factor -= 0.08;
    next30Factor -= 0.06;
  }

  if (driftDirection === 'improving') {
    next7Factor += 0.04;
    next30Factor += 0.06;
  } else if (driftDirection === 'worsening') {
    next7Factor -= 0.07;
    next30Factor -= 0.08;
  }

  if (riskTier === 'high' && daysLate >= 20) {
    next7Factor -= 0.06;
    next30Factor -= 0.08;
  }

  if (tenant.paymentPlanStatus === 'active') {
    next7Factor += 0.05;
    next30Factor += 0.08;
  }

  next7Factor = Math.max(0.15, Math.min(next7Factor, 0.9));
  next30Factor = Math.max(next7Factor, Math.min(next30Factor, 0.95));

  return {
    next7Days: Math.min(balance, Math.round(ervDollars * next7Factor)),
    next30Days: Math.min(balance, Math.round(ervDollars * next30Factor)),
  };
}

function getDataGaps({
  phoneCoveragePct,
  emailCoveragePct,
  historyCoveragePct,
  history6CoveragePct,
  autopilotReadyPct,
}) {
  const gaps = [];

  if (phoneCoveragePct < 70) {
    gaps.push('Phone coverage is below 70%, which limits live SMS and voice automation.');
  }
  if (historyCoveragePct < 50) {
    gaps.push('Fewer than half of tenants have three or more payment records, so trend confidence is still thin.');
  }
  if (history6CoveragePct < 25) {
    gaps.push('Six-month payment history depth is still light, which softens predictive confidence for timing-based forecasts.');
  }
  if (emailCoveragePct < 50) {
    gaps.push('Email coverage is limited, so fallback outreach channels are thinner than they should be.');
  }
  if (autopilotReadyPct < 45) {
    gaps.push('Only a minority of delinquent tenants are fully Autopilot-ready because contact coverage is incomplete.');
  }

  return gaps;
}

function getConfidenceLevel(dataQuality) {
  if (dataQuality.phoneCoveragePct >= 80 && dataQuality.historyCoveragePct >= 60 && dataQuality.history6CoveragePct >= 35) {
    return 'high';
  }
  if (dataQuality.phoneCoveragePct >= 60 && dataQuality.historyCoveragePct >= 35) {
    return 'medium';
  }
  return 'low';
}

function buildHeadline(snapshot) {
  const { riskCounts, metrics } = snapshot;
  if (riskCounts.high > 0) {
    return `${riskCounts.high} high-risk tenants drive ${formatMoney(metrics.atRiskBalance)} of the current action queue`;
  }
  if (metrics.totalBalance > 0) {
    return `${snapshot.metrics.autopilotReady} residents are ready for automated outreach against ${formatMoney(metrics.totalBalance)} in open balance`;
  }
  return 'Portfolio is current enough that Tenafi can shift from triage into monitoring';
}

function buildExecutiveSummary(snapshot) {
  const { metrics, dataQuality, riskCounts } = snapshot;
  return [
    `${snapshot.propertyName || 'This property'} has ${metrics.totalTenants} tracked tenants and ${formatMoney(metrics.totalBalance)} in open balance.`,
    `${riskCounts.high + riskCounts.medium} residents are in the medium/high-risk bands, while the rules engine estimates ${formatMoney(snapshot.projectedCollections.next30Days)} could be recoverable over the next 30 days.`,
    `Confidence is ${dataQuality.confidence} because phone coverage is ${dataQuality.phoneCoveragePct}% and deeper payment history coverage is ${dataQuality.history6CoveragePct}%.`,
  ].join(' ');
}

function buildPortfolioStory(snapshot) {
  const { metrics, segments, lastDataIntake } = snapshot;
  const dataSource = lastDataIntake?.label
    ? `${lastDataIntake.label} data is the latest imported source`
    : 'No recent intake metadata is stored yet';

  return [
    `${dataSource}, and the current queue is concentrated around ${metrics.overdue15Plus} tenants who are 15+ days late.`,
    `${segments.worseningDrift} tenants are drifting later, ${segments.improvingDrift} are improving, and ${metrics.activePlans} active payment plans are already in play.`,
    `${metrics.autopilotReady} delinquent residents are reachable enough for Autopilot today.`,
  ].join(' ');
}

function buildTopActions(snapshot) {
  const topTenant = snapshot.priorityTenants[0];
  const actions = [];

  if (topTenant) {
    actions.push({
      title: `Start with ${topTenant.name}${topTenant.unit ? ` in unit ${topTenant.unit}` : ''}`,
      reason: `${formatMoney(topTenant.currentBalance)} is outstanding, risk is ${topTenant.riskScore}/100, and the tenant sits near the top of projected recovery value.`,
      timing: 'today',
      impact: `Potentially unlocks ${formatMoney(topTenant.ervDollars)} of expected recovery.`,
    });
  }

  if (snapshot.metrics.autopilotReady > 0) {
    actions.push({
      title: `Queue ${snapshot.metrics.autopilotReady} Autopilot-ready residents`,
      reason: 'These residents have balances and enough phone coverage for compliant SMS/voice sequencing.',
      timing: 'today',
      impact: `Creates immediate coverage on ${formatMoney(snapshot.metrics.autopilotReadyBalance)} of outstanding balance.`,
    });
  }

  if (snapshot.dataQuality.gaps.length > 0) {
    actions.push({
      title: 'Close the biggest data gap before scaling automation',
      reason: snapshot.dataQuality.gaps[0],
      timing: 'this week',
      impact: 'Improves forecast confidence and reduces manual cleanup during outreach.',
    });
  }

  if (actions.length < 3 && snapshot.metrics.activePlans > 0) {
    actions.push({
      title: 'Review active payment plans for near-term misses',
      reason: `${snapshot.metrics.activePlans} plans are already active and should be monitored before they drift into higher-risk balances.`,
      timing: 'this week',
      impact: 'Prevents plan slippage from turning into another high-risk bucket.',
    });
  }

  return actions.slice(0, 4);
}

function buildOpportunities(snapshot) {
  const opportunities = [];

  if (snapshot.segments.improvingDrift > 0) {
    opportunities.push(`${snapshot.segments.improvingDrift} residents are trending earlier, which makes plan-first outreach more credible.`);
  }
  if (snapshot.metrics.activePlans > 0) {
    opportunities.push(`${snapshot.metrics.activePlans} active payment plans create a trackable cohort for near-term recovery wins.`);
  }
  if (snapshot.dataQuality.phoneCoveragePct >= 80) {
    opportunities.push('Phone coverage is strong enough to support broader SMS and voice automation once the backend is live.');
  }
  if (snapshot.projectedCollections.next7Days > 0) {
    opportunities.push(`The current signals support roughly ${formatMoney(snapshot.projectedCollections.next7Days)} in potential collections over the next 7 days.`);
  }

  return opportunities.slice(0, 4);
}

function buildOperatorQuestions(snapshot) {
  const questions = [];

  if (snapshot.dataQuality.history6CoveragePct < 25) {
    questions.push('Can we add resident ledgers or monthly delinquency snapshots so timing forecasts have six months of depth?');
  }
  if (snapshot.dataQuality.phoneCoveragePct < 70) {
    questions.push('Which source system can fill missing mobile numbers fastest so more of the queue becomes Autopilot-ready?');
  }
  if (snapshot.metrics.activePlans > 0) {
    questions.push('Which active plans are most likely to miss the next installment and should be reviewed manually this week?');
  }

  return questions.slice(0, 3);
}

export function buildPortfolioSnapshot({ property, tenants, ervMap = new Map() }) {
  const safeProperty = property || {};
  const safeTenants = Array.isArray(tenants) ? tenants : [];

  const enrichedTenants = safeTenants.map((tenant) => {
    const erv = ervMap instanceof Map ? ervMap.get(tenant.id) : null;
    const collectionPotential = computeCollectionPotential({
      currentBalance: tenant.currentBalance,
      ervDollars: erv?.ervDollars,
      expectedPayDate: tenant.fingerprint?.expectedNextPayDate,
      consistency: tenant.fingerprint?.consistency,
      driftDirection: normalizeDirection(tenant.drift?.direction),
      paymentPlanStatus: tenant.paymentPlan?.status,
      riskTier: tenant.risk?.tier,
      daysLate: tenant.currentDaysLate,
    });

    return {
      tenantId: tenant.id,
      name: tenant.name || 'Resident',
      unit: tenant.unit || '',
      currentBalance: Math.round(safeNumber(tenant.currentBalance)),
      monthlyRent: Math.round(safeNumber(tenant.monthlyRent)),
      daysLate: Math.round(safeNumber(tenant.currentDaysLate)),
      riskScore: Math.round(safeNumber(tenant.risk?.score)),
      riskTier: tenant.risk?.tier || 'minimal',
      recommendedAction: tenant.recommendation?.title || tenant.recommendation?.action || tenant.recommendation || 'Review manually',
      insight: tenant.insight || '',
      expectedPayDate: tenant.fingerprint?.expectedNextPayDate || null,
      consistency: tenant.fingerprint?.consistency || 'unknown',
      driftDirection: normalizeDirection(tenant.drift?.direction),
      paymentPlanStatus: tenant.paymentPlan?.status || 'none',
      hasPhone: Boolean(tenant.phone || tenant.phoneE164),
      hasEmail: Boolean(tenant.email),
      paymentHistoryCount: Array.isArray(tenant.paymentHistory) ? tenant.paymentHistory.length : 0,
      partialPaymentCount: Array.isArray(tenant.paymentHistory)
        ? tenant.paymentHistory.filter((entry) => safeNumber(entry.amount) > 0 && safeNumber(entry.amount) < safeNumber(tenant.monthlyRent) * 0.95).length
        : 0,
      lastOutreachAt: Array.isArray(tenant.outreachLog) && tenant.outreachLog.length
        ? tenant.outreachLog
            .map((entry) => entry.date)
            .filter(Boolean)
            .sort()
            .slice(-1)[0]
        : null,
      ervDollars: Math.round(safeNumber(erv?.ervDollars)),
      recoveryProbability: Math.round(safeNumber(erv?.recoveryProbability)),
      projectedCollections: collectionPotential,
    };
  });

  const delinquentTenants = enrichedTenants.filter((tenant) => tenant.currentBalance > 0);
  const totalBalance = sum(delinquentTenants.map((tenant) => tenant.currentBalance));
  const totalMonthlyRent = sum(enrichedTenants.map((tenant) => tenant.monthlyRent));
  const atRiskTenants = enrichedTenants.filter((tenant) => tenant.riskTier === 'high' || tenant.riskTier === 'medium');
  const autopilotReadyTenants = delinquentTenants.filter((tenant) => tenant.hasPhone);
  const history3Plus = enrichedTenants.filter((tenant) => tenant.paymentHistoryCount >= 3);
  const history6Plus = enrichedTenants.filter((tenant) => tenant.paymentHistoryCount >= 6);

  const dataQuality = {
    phoneCoveragePct: Math.round((enrichedTenants.length ? (enrichedTenants.filter((tenant) => tenant.hasPhone).length / enrichedTenants.length) : 0) * 100),
    emailCoveragePct: Math.round((enrichedTenants.length ? (enrichedTenants.filter((tenant) => tenant.hasEmail).length / enrichedTenants.length) : 0) * 100),
    historyCoveragePct: Math.round((enrichedTenants.length ? (history3Plus.length / enrichedTenants.length) : 0) * 100),
    history6CoveragePct: Math.round((enrichedTenants.length ? (history6Plus.length / enrichedTenants.length) : 0) * 100),
    autopilotReadyPct: Math.round((delinquentTenants.length ? (autopilotReadyTenants.length / delinquentTenants.length) : 0) * 100),
  };

  dataQuality.gaps = getDataGaps(dataQuality);
  dataQuality.confidence = getConfidenceLevel(dataQuality);

  const priorityTenants = [...delinquentTenants]
    .sort((left, right) => {
      const leftPriority = safeNumber(left.ervDollars) * 2 + safeNumber(left.riskScore) + safeNumber(left.currentBalance);
      const rightPriority = safeNumber(right.ervDollars) * 2 + safeNumber(right.riskScore) + safeNumber(right.currentBalance);
      return rightPriority - leftPriority;
    })
    .slice(0, 8)
    .map((tenant) => ({
      tenantId: tenant.tenantId,
      name: tenant.name,
      unit: tenant.unit,
      currentBalance: tenant.currentBalance,
      daysLate: tenant.daysLate,
      riskScore: tenant.riskScore,
      riskTier: tenant.riskTier,
      ervDollars: tenant.ervDollars,
      expectedPayDate: tenant.expectedPayDate,
      consistency: tenant.consistency,
      driftDirection: tenant.driftDirection,
      recommendedAction: tenant.recommendedAction,
      reason: tenant.insight || `${tenant.daysLate} days late with ${formatMoney(tenant.currentBalance)} outstanding.`,
      play: tenant.recommendedAction || 'Review next best outreach step.',
    }));

  const snapshot = {
    propertyId: safeProperty.id || '',
    propertyName: safeProperty.name || 'Portfolio',
    generatedAt: new Date().toISOString(),
    metrics: {
      totalTenants: enrichedTenants.length,
      totalBalance: Math.round(totalBalance),
      totalMonthlyRent: Math.round(totalMonthlyRent),
      averageRiskScore: Math.round(average(enrichedTenants.map((tenant) => tenant.riskScore))),
      averageDaysLate: Math.round(average(delinquentTenants.map((tenant) => tenant.daysLate))),
      atRiskBalance: Math.round(sum(atRiskTenants.map((tenant) => tenant.currentBalance))),
      totalExpectedRecovery: Math.round(sum(delinquentTenants.map((tenant) => tenant.ervDollars))),
      autopilotReady: autopilotReadyTenants.length,
      autopilotReadyBalance: Math.round(sum(autopilotReadyTenants.map((tenant) => tenant.currentBalance))),
      activePlans: enrichedTenants.filter((tenant) => tenant.paymentPlanStatus === 'active').length,
      overdue15Plus: delinquentTenants.filter((tenant) => tenant.daysLate >= 15).length,
      overdue30Plus: delinquentTenants.filter((tenant) => tenant.daysLate >= 30).length,
    },
    riskCounts: {
      high: enrichedTenants.filter((tenant) => tenant.riskTier === 'high').length,
      medium: enrichedTenants.filter((tenant) => tenant.riskTier === 'medium').length,
      low: enrichedTenants.filter((tenant) => tenant.riskTier === 'low').length,
      minimal: enrichedTenants.filter((tenant) => tenant.riskTier === 'minimal').length,
    },
    segments: {
      worseningDrift: enrichedTenants.filter((tenant) => tenant.driftDirection === 'worsening').length,
      improvingDrift: enrichedTenants.filter((tenant) => tenant.driftDirection === 'improving').length,
      partialPayPatterns: enrichedTenants.filter((tenant) => tenant.partialPaymentCount > 0).length,
      noRecentOutreach: delinquentTenants.filter((tenant) => !tenant.lastOutreachAt).length,
    },
    dataQuality,
    lastDataIntake: safeProperty.lastDataIntake || null,
    priorityTenants,
  };

  snapshot.projectedCollections = {
    next7Days: Math.round(sum(delinquentTenants.map((tenant) => tenant.projectedCollections.next7Days))),
    next30Days: Math.round(sum(delinquentTenants.map((tenant) => tenant.projectedCollections.next30Days))),
    confidence: dataQuality.confidence,
    basis: 'Deterministic projection anchored to ERV, balance size, expected pay timing, drift direction, and history coverage.',
  };

  return snapshot;
}

export function buildRulesBasedCollectionsBrief(snapshot) {
  if (!snapshot || !snapshot.metrics) {
    return null;
  }

  return {
    propertyId: snapshot.propertyId,
    propertyName: snapshot.propertyName,
    mode: 'rules_preview',
    source: 'tenafi_rules',
    sourceModel: 'deterministic-preview',
    version: 1,
    generatedAt: new Date().toISOString(),
    headline: buildHeadline(snapshot),
    executiveSummary: buildExecutiveSummary(snapshot),
    portfolioStory: buildPortfolioStory(snapshot),
    predictedCollection: snapshot.projectedCollections,
    topActions: buildTopActions(snapshot),
    blindSpots: snapshot.dataQuality.gaps.slice(0, 4),
    opportunities: buildOpportunities(snapshot),
    priorityTenants: snapshot.priorityTenants.slice(0, 5),
    operatorQuestions: buildOperatorQuestions(snapshot),
    confidenceRationale: `This preview is ${snapshot.dataQuality.confidence} confidence because phone coverage is ${snapshot.dataQuality.phoneCoveragePct}% and six-month history coverage is ${snapshot.dataQuality.history6CoveragePct}%.`,
  };
}
